import { useMemo, useState } from "react";
import type {
  ColumnFiltersState,
  PaginationState,
} from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, useRealmRoles } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { PageHeader } from "@/core/components/shared/page-header";
import { Button } from "@/core/components/ui/button";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { useKycCases } from "@/features/kyc/api/use-kyc-cases";
import type { KycCaseFilter } from "@/features/kyc/api/dto";
import { KycCaseFormDialog, kycCasesColumns } from "@/features/kyc/components";
import {
  ReportingGaugesDrawer,
  reportingEndpoints,
  useReportingGauges,
} from "@/features/reporting";

const GAUGE_ROLES = [
  FLASHPAY_ROLES.SUPERADMIN,
  FLASHPAY_ROLES.ADMIN,
  FLASHPAY_ROLES.SERVICE,
] as const;

const KYC_PENDING_REVIEW_GAUGE = ["flashpay_kyc_pending_review"] as const;

/** Traduit les filtres de colonnes DataTableClar en `KycCaseFilter` (§4.2). */
const toKycCaseFilter = (filters: ColumnFiltersState): KycCaseFilter => {
  const filter: KycCaseFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "partyId") filter.partyId = value;
    if (id === "status") filter.status = value as KycCaseFilter["status"];
    if (id === "channel") filter.channel = value as KycCaseFilter["channel"];
  }
  return filter;
};

/** Liste paginée des dossiers KYC — file de travail back-office (POST /KycCases/all). */
export function KycCasesPage() {
  const { t } = useTypedTranslation();
  const realmRoles = useRealmRoles();
  const canGauges = GAUGE_ROLES.some((role) => realmRoles.includes(role));
  const kycGauges = useReportingGauges("kyc", canGauges);
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toKycCaseFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } = useKycCases(params);

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <PageHeader
        title={t("kyc.cases.title")}
        actions={
          <>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-2 w-4 h-4" />
              {t("kyc.cases.create")}
            </Button>
            {canGauges ? (
              <ReportingGaugesDrawer
                gauges={kycGauges.data ?? []}
                names={KYC_PENDING_REVIEW_GAUGE}
                provenanceEndpoints={[reportingEndpoints.kyc.gauges]}
              />
            ) : null}
          </>
        }
      />

      <DataTableClar
        data={data?.items ?? []}
        columnsClar={kycCasesColumns}
        title={t("kyc.cases.title")}
        exportFileName="kyc-cases"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <KycCaseFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}
